import {
  attachFormHandler,
  loadPrompt,
  render,
  type PageView,
  type PendingSubmit,
} from "./app";
import { StorybookApiError, submitExternalResponse, type StorybookPromptPayload } from "./api";
import { logSubmitFailure, logSubmitStart, logSubmitSuccess } from "./diagnostics";

export const STORYBOOK_PAGE_TITLE = "Storybook — Locket";

let loadedPrompt: StorybookPromptPayload | null = null;
let formHandlerAttached = false;
let storyToken: string | null = null;
let pendingSubmit: PendingSubmit | null = null;
let formConsentVersion: string | null = null;
let formClientSubmissionId: string | null = null;

function paint(root: HTMLElement, html: string): void {
  root.innerHTML = html;
}

function setView(root: HTMLElement, view: PageView): void {
  paint(root, render(view));
  document.title = STORYBOOK_PAGE_TITLE;
}

function attachFormIfNeeded(root: HTMLElement, view: PageView, token: string): void {
  if (view.kind !== "form" || formHandlerAttached) return;
  formHandlerAttached = true;
  formConsentVersion = view.consentVersion;
  formClientSubmissionId = crypto.randomUUID();

  attachFormHandler(root, {
    token,
    clientSubmissionId: formClientSubmissionId,
    consentVersion: view.consentVersion,
    getPrompt: () => {
      if (!loadedPrompt) throw new Error("prompt_not_loaded");
      return loadedPrompt;
    },
    onViewChange: (next) => {
      if (next.kind === "form") loadedPrompt = next.prompt;
    },
    onRerender: (html) => paint(root, html),
    onPendingSubmit: (pending) => {
      pendingSubmit = pending;
    },
    onSubmitSuccess: () => {
      pendingSubmit = null;
    },
  });
}

async function loadAndShow(root: HTMLElement, token: string): Promise<void> {
  storyToken = token;
  pendingSubmit = null;
  setView(root, { kind: "loading" });
  const view = await loadPrompt(token);
  if (view.kind === "form") {
    loadedPrompt = view.prompt;
  }
  setView(root, view);
  attachFormIfNeeded(root, view, token);
}

async function retrySubmit(root: HTMLElement): Promise<void> {
  if (!storyToken || !pendingSubmit) return;

  setView(root, { kind: "submitting" });
  logSubmitStart();

  try {
    await submitExternalResponse(
      storyToken,
      pendingSubmit.text,
      pendingSubmit.consentVersion,
      pendingSubmit.clientSubmissionId
    );
    logSubmitSuccess();
    pendingSubmit = null;
    setView(root, { kind: "success", message: "Your answer has been safely sent." });
  } catch (e) {
    if (e instanceof StorybookApiError) {
      logSubmitFailure(e.code);
      if (e.code === "token_completed" || e.code === "response_exists") {
        pendingSubmit = null;
        setView(root, { kind: "already_answered" });
        return;
      }
      if (e.code === "content_required" || e.code === "consent_required") {
        if (loadedPrompt && formConsentVersion && formClientSubmissionId) {
          const formView: PageView = {
            kind: "form",
            prompt: loadedPrompt,
            consentVersion: formConsentVersion,
          };
          setView(root, formView);
          attachFormIfNeeded(root, formView, storyToken);
          queueMicrotask(() => {
            const area = root.querySelector<HTMLTextAreaElement>("#response");
            const box = root.querySelector<HTMLInputElement>("#consent");
            if (area) area.value = pendingSubmit?.text ?? "";
            if (box) box.checked = true;
          });
        }
        return;
      }
    } else {
      logSubmitFailure("unknown");
    }

    setView(root, {
      kind: "unavailable",
      title: "Connection trouble",
      message: "We couldn't reach the server. Check your connection and try again.",
      tone: "neutral",
      retry: "submit",
    });
  }
}

let retryListenerAttached = false;

function attachRetryListener(root: HTMLElement): void {
  if (retryListenerAttached) return;
  retryListenerAttached = true;

  root.addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest("#retry-btn");
    if (!btn) return;

    if (pendingSubmit) {
      void retrySubmit(root);
      return;
    }

    if (storyToken) {
      void loadAndShow(root, storyToken);
    }
  });
}

export async function bootStorybook(root: HTMLElement, token: string | null): Promise<void> {
  attachRetryListener(root);
  document.title = STORYBOOK_PAGE_TITLE;

  if (!token) {
    setView(root, {
      kind: "unavailable",
      title: "Invalid link",
      message: "This page opens from a personal Storybook invitation. Please use the link you were sent.",
    });
    return;
  }

  try {
    await loadAndShow(root, token);
  } catch {
    storyToken = token;
    setView(root, {
      kind: "unavailable",
      title: "Connection trouble",
      message: "We couldn't load this invitation. Check your connection and try again.",
      tone: "neutral",
      retry: "load",
    });
  }
}
